import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Truck, ArrowLeft, Loader2, ShieldCheck, CreditCard, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { trackEvent } from '../components/TrackingPixels';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'Rajasthan',
    pincode: '',
    paymentMethod: 'razorpay'
  });

  const cartTotal = getCartTotal();
  const deliveryCharge = cartTotal > 5000 ? 0 : 99;
  const finalTotal = cartTotal + deliveryCharge;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.phone || !formData.email || !formData.address || !formData.city || !formData.pincode) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!/^\d{6}$/.test(formData.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return false;
    }
    return true;
  };

  const createOrder = async () => {
    const orderData = {
      full_name: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        brand: item.brand || '',
        price: item.discount_price || item.price,
        quantity: item.quantity,
        image: item.images?.[0] || item.image || ''
      })),
      subtotal: cartTotal,
      delivery_charge: deliveryCharge,
      total: finalTotal,
      payment_method: formData.paymentMethod
    };

    const res = await fetch(`${BACKEND_URL}/api/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to create order');
    }
    return res.json();
  };

  const handleRazorpayPayment = useCallback(async (order) => {
    // Step 1: Create Razorpay order on backend
    const rpRes = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: order.id,
        amount: Math.round(finalTotal * 100),
        currency: 'INR'
      })
    });

    if (!rpRes.ok) {
      throw new Error('Failed to initiate payment. Please try again.');
    }

    const rpOrder = await rpRes.json();

    // Step 2: Open Razorpay checkout
    return new Promise((resolve, reject) => {
      const options = {
        key: rpOrder.key_id || RAZORPAY_KEY_ID,
        amount: rpOrder.amount,
        currency: rpOrder.currency,
        name: 'RO Dekho',
        description: `Order #${order.order_number || order.id.slice(0, 8)}`,
        order_id: rpOrder.razorpay_order_id,
        handler: async (response) => {
          try {
            // Step 3: Verify payment on backend
            const verifyRes = await fetch(`${BACKEND_URL}/api/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: order.id
              })
            });

            if (verifyRes.ok) {
              resolve({ success: true, paymentId: response.razorpay_payment_id });
            } else {
              reject(new Error('Payment verification failed'));
            }
          } catch (err) {
            reject(err);
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#1d4ed8'
        },
        modal: {
          ondismiss: () => {
            reject(new Error('Payment cancelled'));
          }
        }
      };

      if (typeof window.Razorpay === 'undefined') {
        reject(new Error('Payment service unavailable. Please refresh the page.'));
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        reject(new Error(response.error?.description || 'Payment failed'));
      });
      rzp.open();
    });
  }, [formData, finalTotal]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }
    if (!validateForm()) return;

    setLoading(true);
    try {
      const order = await createOrder();

      if (formData.paymentMethod === 'razorpay') {
        try {
          await handleRazorpayPayment(order);
          trackEvent.purchase(order);
          clearCart();
          toast.success('Payment successful! Order confirmed.');
          navigate(`/order-success/${order.id}`);
        } catch (payErr) {
          if (payErr.message === 'Payment cancelled') {
            toast.info('Payment cancelled. Your order is saved. You can pay later or choose COD.');
          } else {
            toast.error(payErr.message || 'Payment failed. Please try again.');
          }
        }
      } else {
        // COD flow
        trackEvent.purchase(order);
        clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-success/${order.id}`);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="empty-cart-msg">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add products to your cart before checking out</p>
            <Button onClick={() => navigate('/products')} className="bg-blue-700 hover:bg-blue-800" data-testid="browse-products-btn">
              Browse Products
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-700 mb-6 transition-colors"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="text-blue-700" size={24} />
                  Delivery Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} id="checkout-form" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <Input id="fullName" name="fullName" type="text" required value={formData.fullName} onChange={handleChange} placeholder="Enter your full name" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="checkout-fullname" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} placeholder="10-digit phone number" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="checkout-phone" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="Enter your email" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="checkout-email" />
                  </div>
                  <div>
                    <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">Complete Address *</label>
                    <Textarea id="address" name="address" required value={formData.address} onChange={handleChange} placeholder="House/flat no., street, area" rows={3} className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-500 rounded-lg resize-none" data-testid="checkout-address" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                      <Input id="city" name="city" type="text" required value={formData.city} onChange={handleChange} placeholder="City" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="checkout-city" />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                      <Input id="state" name="state" type="text" value={formData.state} onChange={handleChange} className="w-full px-4 py-6 border-2 border-gray-200 rounded-lg" data-testid="checkout-state" />
                    </div>
                    <div>
                      <label htmlFor="pincode" className="block text-sm font-semibold text-gray-700 mb-2">Pincode *</label>
                      <Input id="pincode" name="pincode" type="text" required value={formData.pincode} onChange={handleChange} placeholder="6-digit" className="w-full px-4 py-6 border-2 border-gray-200 focus:border-blue-500 rounded-lg" data-testid="checkout-pincode" />
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="text-blue-700" size={24} />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Razorpay Option */}
                <label
                  className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.paymentMethod === 'razorpay' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="payment-razorpay-option"
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="razorpay"
                    checked={formData.paymentMethod === 'razorpay'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <CreditCard size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Pay Online (Recommended)</div>
                    <div className="text-sm text-gray-600">UPI, Debit/Credit Card, Net Banking, Wallets</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400">
                    <ShieldCheck size={14} className="text-green-600" />
                    <span className="text-green-600 font-semibold">Secure</span>
                  </div>
                </label>

                {/* COD Option */}
                <label
                  className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="payment-cod-option"
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={formData.paymentMethod === 'cod'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Banknote size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Cash on Delivery (COD)</div>
                    <div className="text-sm text-gray-600">Pay when you receive your order at your doorstep</div>
                  </div>
                </label>

                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                  <Truck size={14} className="text-green-600 shrink-0" />
                  Free installation included with every order. Our technician will install and verify.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100" data-testid={`cart-item-${item.id}`}>
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-md" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm text-gray-900">{item.name}</h4>
                        <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                        <p className="text-sm font-bold text-blue-700">₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span>
                    <span className="font-semibold">₹{cartTotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Delivery</span>
                    <span className="font-semibold">{deliveryCharge === 0 ? <span className="text-green-600">FREE</span> : `₹${deliveryCharge}`}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t border-gray-200">
                    <span>Total</span>
                    <span className="text-blue-700" data-testid="checkout-total">₹{finalTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 py-3">
                  <input
                    type="checkbox"
                    id="smsConsentCheckout"
                    required
                    className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                    data-testid="sms-consent-checkout"
                  />
                  <label htmlFor="smsConsentCheckout" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
                    I hereby Authorize to send notification on SMS/ messages /email / promotional messages.
                  </label>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={loading || cart.length === 0}
                  className="w-full bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <><Loader2 size={20} className="mr-2 animate-spin" />Processing...</>
                  ) : formData.paymentMethod === 'razorpay' ? (
                    <><CreditCard size={20} className="mr-2" />Pay ₹{finalTotal.toLocaleString()}</>
                  ) : (
                    <><Check size={20} className="mr-2" />Place Order (COD)</>
                  )}
                </Button>

                {formData.paymentMethod === 'razorpay' && (
                  <p className="text-center text-xs text-gray-500 mt-1">Secured by Razorpay. 100% safe & encrypted.</p>
                )}
                {formData.paymentMethod === 'cod' && (
                  <p className="text-center text-xs text-gray-500 mt-1">Pay ₹{finalTotal.toLocaleString()} at the time of delivery</p>
                )}

                <div className="grid grid-cols-2 gap-3 text-center text-xs text-gray-600 pt-4 border-t border-gray-200">
                  <div>
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <ShieldCheck size={18} className="text-green-600" />
                    </div>
                    <span>Free Installation</span>
                  </div>
                  <div>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <ShieldCheck size={18} className="text-blue-600" />
                    </div>
                    <span>1 Year Warranty</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
